import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Box, Typography, CircularProgress, Button, Skeleton } from "@mui/material";
import TeamCrest from "./ui/TeamCrest";
import { EmptyState } from "./ui/Pills";
import { fadeUp } from "./ui/motion";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../supabaseClient";
import {
  fetchAllLeagueTeamsForUser,
  resolveOfficialTeamId,
  fetchMyRosterRowForTeam,
} from "../utils/playerLeagueData";

/**
 * Goals & assists per merged league team (official `teams` / `players` row), grouped by team & division.
 */
const PlayerProfile = () => {
  const { user, loading: authLoading } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading || !user) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const teams = await fetchAllLeagueTeamsForUser(user);
        const out = [];

        for (const lt of teams) {
          const officialId = await resolveOfficialTeamId(lt);
          let rosterRow = await fetchMyRosterRowForTeam(user, lt.id);
          const metaName = user.user_metadata?.full_name;
          if (!rosterRow && lt.captain_id === user.id && metaName) {
            rosterRow = { name: metaName, jersey_number: null, email: user.email };
          }

          let playerRow = null;
          if (officialId && rosterRow?.name) {
            const nameTrim = rosterRow.name.trim();
            let res = await supabase
              .from("players")
              .select("id, name, goals, assists, jersey_number, team:team_id(name, logo_url)")
              .eq("team_id", officialId)
              .ilike("name", nameTrim)
              .maybeSingle();
            playerRow = res.data;
            if (!playerRow && rosterRow.jersey_number != null && rosterRow.jersey_number !== "") {
              const j = parseInt(String(rosterRow.jersey_number), 10);
              if (!Number.isNaN(j)) {
                res = await supabase
                  .from("players")
                  .select("id, name, goals, assists, jersey_number, team:team_id(name, logo_url)")
                  .eq("team_id", officialId)
                  .eq("jersey_number", j)
                  .maybeSingle();
                playerRow = res.data;
              }
            }
          }

          out.push({
            leagueTeam: lt,
            officialId,
            rosterName: rosterRow?.name,
            player: playerRow,
          });
        }

        if (!cancelled) setRows(out);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  if (authLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress sx={{ color: "#000" }} />
      </Box>
    );
  }
  if (!user) return <Navigate to="/register" replace />;

  const displayName = user.user_metadata?.full_name || user.email;
  const initial = String(displayName || "?").trim()[0]?.toUpperCase() || "?";

  return (
    <Box sx={{ py: { xs: 1, sm: 3 }, maxWidth: 760, mx: "auto", minHeight: "60vh" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Box sx={{ width: 64, height: 64, borderRadius: "50%", bgcolor: "#0a0a0a", color: "#fff", fontWeight: 900, fontSize: "1.6rem", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {initial}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.7rem", fontWeight: 800, letterSpacing: 2, textTransform: "uppercase", color: "rgba(0,0,0,0.45)" }}>Your profile</Typography>
          <Typography component="h1" noWrap sx={{ fontSize: { xs: "1.5rem", sm: "2rem" }, fontWeight: 900, letterSpacing: "-0.02em", lineHeight: 1.1 }}>
            {displayName}
          </Typography>
          {user.user_metadata?.full_name && (
            <Typography noWrap sx={{ color: "#888", fontSize: "0.88rem" }}>{user.email}</Typography>
          )}
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {[0, 1].map((i) => (
            <Skeleton key={i} variant="rounded" height={132} sx={{ borderRadius: 4 }} />
          ))}
        </Box>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No registrations yet"
          body="Once you register as a team or an individual, your teams and stats show up here."
          action={{ label: "Register for a league", to: "/register/select-division" }}
        />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {rows.map(({ leagueTeam, officialId, rosterName, player }, i) => {
            const teamName =
              leagueTeam.role === "individual" ? player?.team?.name || `${leagueTeam.division || ""} individual` : leagueTeam.name;
            const note = !officialId
              ? leagueTeam.role === "individual"
                ? "Not placed on a team yet"
                : "Stats appear once the schedule is published"
              : !player && rosterName
              ? `No stats row matches “${rosterName}” yet`
              : null;
            return (
              <Box key={leagueTeam.id} sx={{ bgcolor: "#fff", border: "1px solid #ececec", borderRadius: 4, p: { xs: 2, sm: 2.5 }, boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 4px 14px rgba(16,24,40,0.05)", ...fadeUp(i) }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <TeamCrest url={player?.team?.logo_url || leagueTeam.logo_url} name={teamName} size={48} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography noWrap sx={{ fontWeight: 900, fontSize: "1.05rem" }}>{teamName}</Typography>
                    <Typography noWrap sx={{ color: "#888", fontSize: "0.82rem" }}>
                      {[leagueTeam.region, leagueTeam.division].filter(Boolean).join(" · ")}
                      {leagueTeam.role ? ` · ${leagueTeam.role === "individual" ? "Individual" : leagueTeam.role === "captain" ? "Captain" : "Player"}` : ""}
                    </Typography>
                  </Box>
                  {officialId && (
                    <Button component={Link} to={`/team/${officialId}`} size="small" sx={{ fontWeight: 700, color: "#111", borderRadius: 999, flexShrink: 0 }}>
                      Team →
                    </Button>
                  )}
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, mt: 2 }}>
                  {[
                    ["Goals", player?.goals, "#b7860b"],
                    ["Assists", player?.assists],
                    ["Number", player?.jersey_number != null ? `#${player.jersey_number}` : null],
                  ].map(([label, value, tone]) => (
                    <Box key={label} sx={{ bgcolor: "#f7f7f8", borderRadius: 3, px: 1.5, py: 1.25 }}>
                      <Typography sx={{ fontSize: "0.65rem", fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(0,0,0,0.45)" }}>{label}</Typography>
                      <Typography sx={{ fontSize: "1.4rem", fontWeight: 900, fontVariantNumeric: "tabular-nums", color: value != null && value !== 0 ? tone || "#111" : "#c4c4c4" }}>
                        {value ?? "—"}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                {note && <Typography sx={{ mt: 1.25, color: "#888", fontSize: "0.8rem" }}>{note}</Typography>}
              </Box>
            );
          })}
        </Box>
      )}

      <Button component={Link} to="/" sx={{ mt: 3, fontWeight: 700, color: "#111" }}>
        ← Home
      </Button>
    </Box>
  );
};

export default PlayerProfile;
